(ns minorodata.components.dynamic-docs.sidebar
  (:require
   ["lucide-react" :refer [ChevronRight Search]]
   ["react" :as react]
   [applied-science.js-interop :as js-interop]
   [applied-science.js-interop :as jsi]
   [bleene.dag.context-menu :refer [launch-transform]]
   [goog.functions :refer [debounce]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :as utils]
   [minorodata.components.dag.utils :refer [go-to-dag]]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [reitit.frontend.easy :as rt.easy]
   [ui.context-menu]
   [ui.dropdown-menu :refer [dropdown-menu-separator]]
   [ui.fields :refer [input]]
   [ui.popover :refer [popover-anchor popover-content popover-root]]
   [ui.utils :refer [clsx]]))

(defn apply-search [prop value]
  (dispatch [:transformations-search/update {prop value}])
  (dispatch [:transformations.events/do-search]))

(def term-debounce-timeout 600)

(def apply-search-term-debounce
  (debounce
   (fn [value]
     (apply-search :transformations-search/term value))
   term-debounce-timeout))

(defn apply-search-term [event]
  (let [value (js-interop/get-in event [:target :value])]
    (apply-search-term-debounce value)))


(defn loading-placeholder
  []
  [:div {:class "flex flex-col gap-2 my-2 animate-kleene-pulse"}
   [:div {:class "h-2 rounded-full bg-kleene-selected-background w-11/12"}]
   [:div {:class "h-2 rounded-full bg-kleene-selected-background opacity-70 w-9/12"}]
   [:div {:class "h-2 rounded-full bg-kleene-selected-background opacity-40 w-10/12"}]])

(defn search-dropdown
  [{:keys [on-close on-select only-transforms]}]
  (let [route-name         @(re-posh/subscribe [:app-route/prop :app-route/name])
        route-params       @(re-posh/subscribe [:app-route/prop :app-route/params])
        searching?         @(re-posh/subscribe [:mold-http/loading? :transformations.api/load-search-results])
        {:keys [matched]}  @(re-posh/subscribe [:transformations.subs/search-results])

        on-click on-close]
    [:div
     (if searching?
       [:div {:class "py-2"} [loading-placeholder]]
       [:div {:class "-my-2"}
        (for [{:keys [id master-id name type]} (take 5 (if only-transforms
                                                         (filter #(= :transform (:type %)) matched)
                                                         matched))]
          (let [[subline href]
                (case type
                  :group     ["Group" (rt.easy/href route-name route-params {:group-id id})]
                  :transform ["Transform" (rt.easy/href :dynamic-docs-details {:entity-id master-id})]
                  nil)]
            [:a {:href     (when-not on-select href)
                 :on-click (fn []
                             (on-click id)
                             (when on-select
                               (on-select id)))
                 :class    "flex gap-2 justify-between items-baseline py-2 border-b text-kleene-text border-b-kleene-layout-border last:border-b-0 hover:text-kleene-text/60 transition-colors"}
             [:span {:class "whitespace-nowrap overflow-hidden flex-1 text-ellipsis"} name]
             [:span {:class "text-xs text-kleene-text-secondary"} subline]]))])]))

(defn search-with-popover [{:keys [placeholder name default-value on-change open set-open on-open-change class align]}
                           content]
  [:div {:class "search-dropdown relative"}
   [:div {:class "relative"}
    [popover-root {:open           open
                   :modal          false
                   :on-open-change on-open-change}
     [popover-anchor {:as-child true}
      [:div
       [input {:class         "min-w-0"
               :placeholder   placeholder
               :default-value default-value
               :on-change     (fn [e]
                                (when (fn? set-open)
                                  (set-open (boolean (not-empty (jsi/get-in e [:target :value])))))
                                (on-change e))
               :name          name
               :input-props   {:auto-complete "off"}
               :icon          Search}]]]

     [popover-content {:class (clsx "w-full max-w-fit" class)
                       :align (or align "start")
                       :on-open-auto-focus (fn [e] (.preventDefault e))}
      content]]]])

(defn search-input
  []
  (let [show-dropdown?   (reagent/atom false)
        search           @(re-posh/subscribe [:transformations-search/prop :transformations-search/term])

        hide-dropdown    #(reset! show-dropdown? false)
        on-change        #(let [v (some-> % .-target .-value)]
                            (when (and (not @show-dropdown?) (not-empty v))
                              (reset! show-dropdown? true))
                            (when (empty? v)
                              (reset! show-dropdown? false))
                            (apply-search-term %))]
    (fn [{:keys [on-select only-transforms]}]
      [search-with-popover {:placeholder    "Search by name"
                            :default-value  search
                            :name           "search-entity"
                            :on-change      on-change
                            :open           @show-dropdown?
                            :set-open       #(reset! show-dropdown? %)
                            #_#_:on-open-change (fn [v]
                                                  (when (and @show-dropdown? (not v))
                                                    (hide-dropdown)))}
       [search-dropdown {:on-select       on-select
                         :only-transforms only-transforms
                         :on-close        hide-dropdown}]])))

(defn on-launch [transform option-id]
  (let [{:transform/keys [id master-id group-id]} transform]
    (launch-transform id group-id option-id master-id)))



(defn list-link
  [{:keys [name href on-select is-active? transform]}]
  [ui.context-menu/context-menu
   {:content [:<>
              [ui.context-menu/context-menu-item {:on-click (fn []
                                                              (go-to-dag transform))}
               "Show in Pipelines"]
              [ui.context-menu/context-menu-sub
               {:content [:<>
                          [ui.context-menu/context-menu-item
                           {:on-click #(on-launch transform :single)}
                           "Only this one"]

                          [ui.context-menu/context-menu-item
                           {:on-click #(on-launch transform :upstream)}
                           "Upstream"]

                          [ui.context-menu/context-menu-item
                           {:on-click #(on-launch transform :downstream)}
                           "Downstream"]]}

               [:span {:class "flex items-center gap-2 justify-between w-full"}
                [:span "Run"]
                [:> ChevronRight {:size 16}]]]

              [dropdown-menu-separator]

              [ui.context-menu/context-menu-item {:data-cy  "sql-console-modal-sidebar-menu-delete-transform-btn"
                                                  :class    "text-kleene-error-01"
                                                  :on-click (fn []
                                                              (dispatch [:app.events/navigate-query {:delete-transform true
                                                                                                     :transform-id     (or (:transform/id transform)
                                                                                                                           (:transform/transform-id transform))
                                                                                                     :group-id         (:transform/group-id transform)}]))}
               "Delete"]]}
   [(if href :a :span)
    {:class    (clsx "py-1 px-2 text-ellipsis whitespace-nowrap overflow-hidden rounded-lg transition-opacity cursor-pointer"
                     {"bg-kleene-selected-background" is-active?
                      "hover:opacity-60" (not is-active?)})
     :title    name
     :href     href
     :on-click on-select}
    name]])

(defn transforms-list-items
  [{:keys [transforms selected on-select]}]
  (let [selected (if (set? selected) selected #{selected})]
    [:<>
     (for [{:transform/keys [id name master-id] :as transform} transforms]
       ^{:key master-id}
       [list-link {:name       name
                   :href       (when-not on-select (rt.easy/href :dynamic-docs-details {:entity-id master-id}))
                   :is-active? (selected master-id)
                   :transform  transform
                   :on-select  #(on-select id transform)}])]))

(defn transforms-not-found
  []
  [:div {:class "pl-6 text-kleene-icon"}
   "No transforms found."])

(defn transforms-list [{:keys [group-id]}]
  (dispatch [:transform.api/fetch-transforms {:group-id       group-id
                                              :items-per-page 1000}])
  (fn [{:keys [selected on-select]}]
    (let [{:group-transforms/keys [transforms id]}
          @(re-posh/subscribe [:group-transforms/pull [:group-transforms/id group-id] '[* {:group-transforms/transforms [*]}]])

          transforms (->> transforms
                          (filter (comp not :transform/environment-id))
                          (sort-by :transform/order))
          loading?   @(re-posh/subscribe [:mold-http/loading-id? :transform.api/fetch-transforms (keyword (str "fetch-transforms-request-" group-id))])
          not-found? (and id (not loading?) (empty? transforms))]
      [:ul {:class "flex flex-col pl-3.5"}
       (cond
         not-found? [transforms-not-found]
         loading?   [loading-placeholder]
         transforms [transforms-list-items {:transforms transforms :selected selected :on-select on-select}])])))

(defn group-list-button [{:keys [component expanded mark on-click]} text]
  [(or component :button)
   {:class    "flex gap-0.5 py-1 cursor-pointer group text-kleene-text w-full"
    :on-click on-click}
   [:div {:class (clsx "transition-all text-kleene-icon"
                       {"rotate-90" expanded})}
    [:> ChevronRight {:size 20 :class "leading-zero align-top"}]]
   [:span {:class "group-hover:opacity-60 transition-opacity align-left whitespace-nowrap text-ellipsis font-medium overflow-hidden"}
    text]
   (when mark
     [:div {:class "flex justify-end items-center flex-1 ml-1"} mark])])

(defn get-master-transform-group-id [id]
  (get-in
   @(re-posh/subscribe [:transform-master/pull
                        [:transform-master/master-id id]
                        '[* {:transform-master/transform [*]}]])
   [:transform-master/transform :transform/group-id]))


(defn group-list-group
  [{:keys [group-id selected query-group-id]}]
  (let [[expanded? set-expanded?] (react/useState false)
        node-ref (react/useRef nil)
        selected-group-id (get-master-transform-group-id selected)
        expand (fn []
                 (set-expanded? true)
                 (utils/scroll-into-view (.-current node-ref)))

        {:keys [name]} @(re-posh/subscribe [:group.subs/panel-group group-id])]

    (react/useEffect
     (fn []
       (when (= group-id selected-group-id)
         (expand))
       (fn []))
     #js [selected-group-id])

    (react/useEffect
     (fn []
       (when (= group-id query-group-id)
         (expand))
       (fn []))
     #js [query-group-id])

    [:div {:ref node-ref}
     [group-list-button {:on-click (fn []
                                     (set-expanded? not)
                                     (when (= group-id query-group-id)
                                       (dispatch [:app.events/navigate-query {:group-id nil}])))
                         :expanded expanded?}
      name]

     (when expanded?
       [transforms-list {:group-id  group-id
                         :selected  selected}])]))

(defn groups-list
  [{:keys [selected groups group-button-props]}]
  (let [query @(re-posh/subscribe [:app-route/prop :app-route/query])]
    [:div {:class "flex flex-col"}
     (for [{:group/keys [id]} groups]
       ^{:key id}
       [:f> group-list-group {:group-id       id
                              :selected       selected
                              :query-group-id (:group-id query)
                              :group-button-props group-button-props}])]))

(defn groups-with-search [{:keys [loading? on-select only-transforms]} & children]
  [:div {:class "flex flex-col gap-2"}
   [:div {:class "p-2 bg-kleene-background"}
    [search-input {:on-select on-select
                   :only-transforms only-transforms}]]
   (into [:div {:class "px-2 flex-1"}]
         (if loading?
           [loading-placeholder]
           children))])

(defn sidebar
  []
  (let [{:app-route/keys [params]} @(re-posh/subscribe [:app-route/pull])

        groups   @(re-posh/subscribe [:group.subs/ordered-groups])
        loading? @(re-posh/subscribe [:mold-http/loading? :group.api/fetch-groups])]
    [:div {:class "p-2"}
     [groups-with-search {}
      [groups-list {:selected  (:entity-id params)
                    :groups    groups
                    :loading?  loading?}]]]))
